import { getSpaceOwnerLabel } from '../../services/spaceMembersService';

/**
 * Espacio ajeno: `GET /getmylots?owner_user_id=` mezcla los marcadores del owner
 * (`item_type: 'marker'`, `is_favorite`), que son personales. Quita los
 * marcadores puros y apaga `is_favorite` en el resto. `lot_content` se queda.
 *
 * @see docs/maps/espacios-invite.md (Permisos del invitado)
 * @param {Array<object>} lots Filas ya normalizadas de `getmylots`.
 * @returns {{ lots: Array<object>, removed: number }} `removed` = marcadores puros quitados.
 */
export function stripOwnerFavoritesFromLots(lots) {
  const rows = Array.isArray(lots) ? lots : [];
  const kept = rows
    .filter((lot) => lot?.item_type !== 'marker')
    .map((lot) => (lot?.is_favorite ? { ...lot, is_favorite: false } : lot));
  return { lots: kept, removed: rows.length - kept.length };
}

/**
 * Owner a mandar como `owner_user_id` en las lecturas F1 del Dock (contenido,
 * UF, media) y en las escrituras aditivas F2 del invitado (mismo valor en
 * query o body según el endpoint). Solo en espacio ajeno y fuera de vistas
 * `readOnly` (shares por token, que tienen su propio contrato). En el propio
 * espacio → `null` y las requests salen igual que siempre.
 *
 * @see docs/maps/espacios-invite.md (Lecturas F1 / Escrituras F2 del invitado)
 * @param {{ isViewingSharedSpace?: boolean, activeOwnerUserId?: string|null, readOnly?: boolean }} ctx
 * @returns {string|null}
 */
export function resolveSpaceMirrorReadOwner({
  isViewingSharedSpace = false,
  activeOwnerUserId = null,
  readOnly = false,
} = {}) {
  if (!isViewingSharedSpace || readOnly) return null;
  const owner = activeOwnerUserId ? String(activeOwnerUserId).trim() : '';
  return owner || null;
}

/** Título del bloque privado en shares por token (`readOnly`): sin cambios. */
export const PRIVATE_BLOCK_TITLE_DEFAULT = 'Solo vos';
/** Propio espacio: los invitados al espacio también lo ven. */
export const PRIVATE_BLOCK_TITLE_OWN_SPACE = 'Solo en tu espacio';
/** Espacio ajeno sin nombre / marca / mail del owner. */
export const PRIVATE_BLOCK_TITLE_SHARED_FALLBACK = 'Espacio compartido';

/**
 * Título del bloque privado (Información privada del lote y detalle de UF).
 * - `readOnly` (shares por token) → "Solo vos", como siempre.
 * - Propio espacio → "Solo en tu espacio".
 * - Espacio ajeno → label del owner como en el chip (`space_name` o
 *   "Espacio de {owner_display_name|owner_email}"); sin datos → "Espacio compartido".
 *
 * @see docs/maps/espacios-invite.md (Lecturas del invitado, F1)
 * @param {{ readOnly?: boolean, spaceReadOwnerUserId?: string|null, activeSpace?: object|null }} ctx
 * @returns {string}
 */
/** ¿El espacio trae algún dato para armar el label del chip (sin caer al id)? */
const hasSpaceOwnerLabel = (activeSpace) => ['space_name', 'owner_display_name', 'owner_email']
  .some((key) => String(activeSpace?.[key] || '').trim());

export function resolvePrivateBlockTitle({
  readOnly = false,
  spaceReadOwnerUserId = null,
  activeSpace = null,
} = {}) {
  if (readOnly) return PRIVATE_BLOCK_TITLE_DEFAULT;
  if (!spaceReadOwnerUserId) return PRIVATE_BLOCK_TITLE_OWN_SPACE;
  return hasSpaceOwnerLabel(activeSpace)
    ? getSpaceOwnerLabel(activeSpace)
    : PRIVATE_BLOCK_TITLE_SHARED_FALLBACK;
}

/** Campos de permisos por UF que manda la API (mig 082). */
const UF_PERMISSION_KEYS = ['created_by_user_id', 'can_edit_all', 'can_delete'];

/**
 * Solo los campos de permisos de una UF (`getuserlotdetail` / fila de
 * `getuserlotunits`) que vinieron en la respuesta; los ausentes no se pisan.
 *
 * @param {object|null|undefined} source
 * @returns {object}
 */
export function pickUfPermissionFields(source) {
  if (!source || typeof source !== 'object') return {};
  return UF_PERMISSION_KEYS.reduce((acc, key) => {
    if (key in source) acc[key] = source[key];
    return acc;
  }, {});
}

/**
 * Permisos del invitado sobre una UF del owner en espacio ajeno. Salen de la
 * API (`can_edit_all` / `can_delete`, mig 082): `true` solo si la creó él. No
 * se calculan en el Front. Ausente / `false` → regla acotada de F2.
 * Propio espacio y shares: no usar (la lógica del dueño no cambia).
 *
 * @param {object|null|undefined} unit - detalle o fila de la UF.
 * @returns {{ canEditAll: boolean, canDelete: boolean }}
 */
export function resolveSpaceGuestUnitAccess(unit) {
  return {
    canEditAll: unit?.can_edit_all === true,
    canDelete: unit?.can_delete === true,
  };
}

/**
 * Payload de `createuserlot` del invitado: `is_public` es solo del owner (la
 * API lo ignora en el alta y lo conserva en el update), así que no se manda.
 *
 * @param {object} payload
 * @param {string|null|undefined} ownerUserId - owner del espacio (null = propio).
 * @returns {object}
 */
export function stripSpaceGuestUfPayload(payload, ownerUserId) {
  if (!ownerUserId || !payload || typeof payload !== 'object') return payload;
  const { is_public: _omit, ...rest } = payload;
  return rest;
}

/**
 * Descarte del borrador de UF (paso 1 del wizard), igual que el dueño. Se borra
 * con el owner del espacio donde se creó (aunque el usuario haya cambiado de
 * espacio después) y la lista de UF solo se refresca si sigue en ese espacio.
 *
 * @param {{ draftOwnerUserId?: string|null, currentOwnerUserId?: string|null }} args
 * @returns {{ deleteOptions: { ownerUserId: string|null }, refreshUnits: boolean }}
 */
export function resolveUfDraftDiscard({ draftOwnerUserId = null, currentOwnerUserId = null } = {}) {
  const draftOwner = draftOwnerUserId || null;
  return {
    deleteOptions: { ownerUserId: draftOwner },
    refreshUnits: draftOwner === (currentOwnerUserId || null),
  };
}

/** Empty state de listas para el invitado en espacio ajeno (panel Listas y selector del Dock). */
export const SPACE_GUEST_EMPTY_LISTS_TITLE = 'Este espacio todavía no tiene listas';
export const SPACE_GUEST_EMPTY_LISTS_HINT = 'Creá la primera lista de este espacio.';

/**
 * Subtítulo del índice de Listas para el invitado en espacio ajeno. Usa el
 * mismo label que el chip (`space_name` o "Espacio de {owner}"); sin datos →
 * "este espacio compartido".
 *
 * @param {object|null|undefined} activeSpace Espacio activo (`getsharedwithmespaces`).
 * @returns {string}
 */
export function resolveSpaceGuestListsSubtitle(activeSpace) {
  const label = hasSpaceOwnerLabel(activeSpace)
    ? getSpaceOwnerLabel(activeSpace)
    : 'este espacio compartido';
  return `Estás colaborando en ${label}: podés ver y agregar, no borrar.`;
}

/**
 * Detalle de una UF del owner en espacio ajeno: `data` de
 * `getuserlotdetail?owner_user_id=` si vino; si no (403 / 404 / API sin
 * desplegar / error), la fila de `getuserlotunits` (sin `observations`).
 *
 * @param {object|null|undefined} detailResponse JSON de `getUserLotDetail` (o null si falló).
 * @param {object} unitRow Fila de `getuserlotunits?owner_user_id=`.
 * @returns {object}
 */
export function pickSpaceMirrorUnitDetail(detailResponse, unitRow) {
  const data = detailResponse?.data;
  return data && typeof data === 'object' && !Array.isArray(data) ? data : unitRow;
}

/** Toast del invitado cuando la API rechaza una escritura en espacio ajeno (F2). */
export const SPACE_GUEST_WRITE_FORBIDDEN_MESSAGE = 'No tenés permiso para hacer cambios en este espacio. Puede que el dueño te haya quitado el acceso.';
export const SPACE_GUEST_WRITE_NOT_FOUND_MESSAGE = 'No encontramos este elemento en el espacio. Puede que el dueño lo haya quitado.';
export const SPACE_GUEST_READ_FORBIDDEN_MESSAGE = 'No tenés acceso a las unidades de este espacio. Puede que el dueño te haya quitado el acceso.';
export const SPACE_GUEST_LIST_CHANGED_MESSAGE = 'La lista cambió mientras la editabas. Actualizamos el contenido; volvé a intentar.';
export const SPACE_GUEST_FOREIGN_LISTS_MESSAGE = 'Alguna de las listas elegidas no es de este espacio. Quitala y volvé a intentar.';
export const SPACE_GUEST_EDITOR_FIELDS_MESSAGE = 'En este espacio solo podés cambiar la publicación (título, descripción, precio, moneda, operación o estado). No se guardaron los cambios.';

/** Prefijo del 400 de la API cuando el editor no manda ningún campo de publicación. */
const SPACE_GUEST_EDITOR_FIELDS_API_PREFIX = 'El editor solo puede cambiar';

/**
 * Mensaje claro para un error de una escritura del invitado (F2). Solo
 * aplica en espacio ajeno (`ownerUserId`); en el propio espacio devuelve null
 * y cada pantalla sigue con su toast de siempre.
 *
 * - 403 → sin permiso.
 * - 404 → no encontrado; con `notFoundAsForbidden` (p. ej. `/updateuserlotlist`,
 *   que responde 404 si ya no sos miembro) → sin permiso.
 * - 400 "El editor solo puede cambiar…" (`message`) → solo campos de publicación.
 * - 403 con `foreign_list_ids` (`createuserlot`) → listas que no son del owner.
 *
 * @param {number|null|undefined} status HTTP status de la respuesta.
 * @param {string|null|undefined} ownerUserId Owner del espacio activo (null = propio).
 * @param {{ message?: string|null, notFoundAsForbidden?: boolean, foreignListIds?: string[]|null }} [options]
 * @returns {string|null}
 */
export function resolveSpaceGuestWriteError(status, ownerUserId, {
  message,
  notFoundAsForbidden = false,
  foreignListIds = null,
} = {}) {
  if (!ownerUserId) return null;
  if (status === 403 && Array.isArray(foreignListIds) && foreignListIds.length > 0) {
    return SPACE_GUEST_FOREIGN_LISTS_MESSAGE;
  }
  if (status === 403) return SPACE_GUEST_WRITE_FORBIDDEN_MESSAGE;
  if (status === 404) {
    return notFoundAsForbidden ? SPACE_GUEST_WRITE_FORBIDDEN_MESSAGE : SPACE_GUEST_WRITE_NOT_FOUND_MESSAGE;
  }
  if (status === 400 && String(message || '').includes(SPACE_GUEST_EDITOR_FIELDS_API_PREFIX)) {
    return SPACE_GUEST_EDITOR_FIELDS_MESSAGE;
  }
  return null;
}

/**
 * 403 de `/updateuserlotlist` porque la selección del invitado no incluye todo
 * lo actual (`removed`). Pasa si el dueño agregó ítems mientras tanto: hay que
 * refrescar la lista, no reintentar con la selección vieja.
 *
 * @param {object|null|undefined} res Respuesta del servicio.
 * @returns {boolean}
 */
export function isSpaceGuestListRemovalError(res) {
  return Boolean(res) && res._httpStatus === 403 && Array.isArray(res.removed);
}
